import { Button, Input, Space, Spin, Tabs, Tag } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import {
  ProTable,
  type ProColumns,
} from '@ant-design/pro-components';
import type { CurrentUser } from '@cell-bank/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatTenantDate } from '../lib/tenant';

type CellLine = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  species: string;
  originType: string;
  status: string;
};

type CellMaterial = {
  id: string;
  name: string;
  type?: string;
};

type StorageLocation = {
  id: string;
  path: string;
  displayPath?: string;
  type?: string;
};

type StoragePosition = {
  id: string;
  path: string;
  displayPath?: string;
};

type StorageTreeNode = {
  title: string;
  value: string;
  key: string;
  pathLabel: string;
  children?: StorageTreeNode[];
};

type Sample = {
  id: string;
  sampleNo: string;
  barcode: string | null;
  cellLineId: string;
  cellMaterialId: string | null;
  materialName: string | null;
  passage: string | null;
  status: string;
  version: number;
  storagePositionId: string | null;
  qcStatus: string;
};

type Transaction = {
  id: string;
  transactionNo: string;
  operationType: string;
  requestNo: string | null;
  executedByName: string | null;
  executedAt: string;
  items: {
    cellLineCode?: string | null;
    materialName?: string | null;
    passage?: string | null;
  }[];
};

const operationTypes = [
  'PUTAWAY',
  'CONSUME',
  'LOAN',
  'RETURN',
  'MOVE',
  'DISPOSE',
  'QUARANTINE',
  'CORRECT',
];
const sampleStatuses = [
  'DRAFT',
  'AVAILABLE',
  'RESERVED',
  'LOANED',
  'CONSUMED',
  'DISPOSED',
  'QUARANTINED',
  'MISSING',
];
const qcStatuses = ['NOT_TESTED', 'PENDING', 'PASSED', 'FAILED'];
const originTypes = ['COMMERCIAL', 'IN_HOUSE', 'DERIVED', 'OTHER'];
const speciesOptions = [
  'Homo sapiens',
  'Mus musculus',
  'Rattus norvegicus',
  'Cricetulus griseus',
  'Chlorocebus sabaeus',
  'Mesocricetus auratus',
];
const allOperationTypes = 'ALL';

function buildStorageTreeData(source: StorageLocation[]): StorageTreeNode[] {
  const nodesByPath = new Map(source.map((item) => [item.path, item]));
  const roots: StorageTreeNode[] = [];
  const getChild = (
    siblings: StorageTreeNode[],
    value: string,
    title: string,
    pathLabel: string,
  ) => {
    let child = siblings.find((item) => item.value === value);
    if (!child) {
      child = { title, value, key: value, pathLabel, children: [] };
      siblings.push(child);
    }
    return child;
  };
  for (const location of source) {
    const pathParts = location.path.split('/').filter(Boolean);
    const displayParts = (location.displayPath ?? location.path)
      .split('/')
      .filter(Boolean);
    let siblings = roots;
    pathParts.forEach((part, index) => {
      const path = `/${pathParts.slice(0, index + 1).join('/')}`;
      const node = nodesByPath.get(path);
      const child = getChild(
        siblings,
        node?.id ?? `path:${path}`,
        displayParts[index] ?? part,
        displayParts.slice(0, index + 1).join(' / '),
      );
      if (index < pathParts.length - 1) siblings = child.children ?? [];
    });
  }
  return roots;
}

export function SearchPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const initialParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const initialQuery = initialParams.get('q') ?? initialParams.get('query') ?? '';
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [queryInput, setQueryInput] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [activeTab, setActiveTab] = useState(initialParams.get('tab') ?? 'samples');
  const [cellLines, setCellLines] = useState<CellLine[]>([]);
  const [materials, setMaterials] = useState<CellMaterial[]>([]);
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);
  const [storagePositions, setStoragePositions] = useState<StoragePosition[]>([]);
  const [selectedSamples, setSelectedSamples] = useState<Sample[]>([]);

  const canReadSamples = currentUser?.permissions.includes('sample:read') ?? false;
  const canReadCellLines = currentUser?.permissions.includes('cell_line:read') ?? false;
  const canReadTransactions = currentUser?.permissions.includes('audit:read') ?? false;
  const canRequestCheckout = (() => {
    if (!currentUser?.permissions.includes('inventory:request')) return false;
    const setting = currentUser.tenant.inventoryOperationSettings?.CONSUME;
    const canUseManagerOperations = currentUser.permissions.includes('inventory:execute');
    return (setting?.enabled ?? true) && ((setting?.userVisible ?? true) || canUseManagerOperations);
  })();

  useEffect(() => {
    if (!currentUser) return;
    const permittedTabs = [
      canReadSamples ? 'samples' : null,
      canReadCellLines ? 'cell-lines' : null,
      canReadTransactions ? 'transactions' : null,
    ].filter((value): value is string => Boolean(value));
    if (permittedTabs.length && !permittedTabs.includes(activeTab)) {
      const firstTab = permittedTabs[0];
      if (firstTab) setActiveTab(firstTab);
    }
  }, [activeTab, canReadCellLines, canReadSamples, canReadTransactions, currentUser]);

  useEffect(() => {
    const load = async () => {
      const me = await fetch('/api/v1/auth/me', { credentials: 'include' });
      if (me.status === 401) {
        window.location.assign('/login');
        return;
      }
      if (!me.ok) return;
      const user = (await me.json()) as CurrentUser;
      setCurrentUser(user);
      const needLines = user.permissions.includes('sample:read') || user.permissions.includes('cell_line:read');
      const needLocations = user.permissions.includes('sample:read') || user.permissions.includes('audit:read');
      const [lines, materialsResponse, locations, positions] = await Promise.all([
        needLines
          ? fetch('/api/v1/cell-lines?limit=100', { credentials: 'include' })
          : Promise.resolve(undefined),
        user.permissions.includes('sample:read')
          ? fetch('/api/v1/cell-materials?limit=200', { credentials: 'include' })
          : Promise.resolve(undefined),
        needLocations
          ? fetch('/api/v1/storage-locations', { credentials: 'include' })
          : Promise.resolve(undefined),
        user.permissions.includes('sample:read')
          ? fetch('/api/v1/storage-positions?availableOnly=false', { credentials: 'include' })
          : Promise.resolve(undefined),
      ]);
      if (lines?.ok) setCellLines(((await lines.json()) as { items: CellLine[] }).items);
      if (materialsResponse?.ok)
        setMaterials(((await materialsResponse.json()) as { items: CellMaterial[] }).items);
      if (locations?.ok) {
        const payload = (await locations.json()) as StorageLocation[] | { items?: StorageLocation[] };
        setStorageLocations(Array.isArray(payload) ? payload : (payload.items ?? []));
      }
      if (positions?.ok)
        setStoragePositions(((await positions.json()) as { items: StoragePosition[] }).items);
    };
    void load();
  }, []);

  const cellLineById = useMemo(() => new Map(cellLines.map((item) => [item.id, item])), [cellLines]);
  const materialById = useMemo(() => new Map(materials.map((item) => [item.id, item])), [materials]);
  const positionById = useMemo(() => new Map(storagePositions.map((item) => [item.id, item])), [storagePositions]);
  const storageTreeData = useMemo(() => buildStorageTreeData(storageLocations), [storageLocations]);
  const storageTreeExpandedKeys = useMemo(
    () => storageLocations.filter((item) => item.type === 'SITE' || item.type === 'DEVICE').map((item) => item.id),
    [storageLocations],
  );
  const operationLabel = (value: string) => t(`transactions.operationTypes.${value}`, { defaultValue: value });
  const sampleStatusLabel = (value: string) => t(`samples.statusOptions.${value}`, { defaultValue: value });
  const originTypeLabel = (value: string) => t(`cellLines.originTypeOptions.${value}`, { defaultValue: value });
  const qcStatusLabel = (value: string) => t(`samples.qcStatuses.${value}`, { defaultValue: value });

  const requestCheckout = (samples: Sample[]) => {
    const availableSamples = samples.filter((sample) => sample.status === 'AVAILABLE');
    if (!availableSamples.length) return;
    void navigate('/requests', {
      state: {
        operationType: 'CONSUME',
        requestSamples: availableSamples.map((sample) => ({
          sampleId: sample.id,
          sampleNo: sample.sampleNo,
          version: sample.version,
          status: sample.status,
          storagePositionId: sample.storagePositionId,
          cellLineCode: cellLineById.get(sample.cellLineId)?.code ?? sample.cellLineId,
          materialName: sample.materialName ?? materialById.get(sample.cellMaterialId ?? '')?.name ?? null,
          passage: sample.passage,
          storagePositionLabel: positionById.get(sample.storagePositionId ?? '')?.displayPath ?? positionById.get(sample.storagePositionId ?? '')?.path ?? sample.storagePositionId,
        })),
      },
    });
  };

  const updateUrl = (tab = activeTab, nextQuery = query) => {
    const params = new URLSearchParams();
    if (tab) params.set('tab', tab);
    if (nextQuery) params.set('q', nextQuery);
    void navigate(`/search?${params.toString()}`, { replace: true });
  };
  const submitQuery = (value: string) => {
    const nextQuery = value.trim();
    setQueryInput(nextQuery);
    setQuery(nextQuery);
    updateUrl(activeTab, nextQuery);
  };
  const changeTab = (tab: string) => {
    setActiveTab(tab);
    updateUrl(tab, query);
  };

  const append = (params: URLSearchParams, key: string, value: unknown) => {
    if (typeof value === 'string' && value.trim()) params.set(key, value.trim());
  };
  const requestSamples = async (params: Record<string, unknown>) => {
    const search = new URLSearchParams({ limit: '100' });
    append(search, 'query', query);
    append(search, 'cellLineId', params.cellLineId);
    append(search, 'cellMaterialId', params.cellMaterialId);
    append(search, 'status', params.status);
    append(search, 'storageLocationId', params.storageLocationId);
    append(search, 'qcStatus', params.qcStatus);
    const response = await fetch(`/api/v1/samples?${search}`, { credentials: 'include' });
    if (response.status === 401) {
      window.location.assign('/login');
      return { data: [], success: false, total: 0 };
    }
    if (!response.ok) throw new Error(t('search.loadFailed'));
    const body = (await response.json()) as { items: Sample[] };
    return { data: body.items, success: true, total: body.items.length };
  };
  const requestCellLines = async (params: Record<string, unknown>) => {
    const search = new URLSearchParams({ limit: '100' });
    append(search, 'query', query);
    append(search, 'species', params.species);
    append(search, 'originType', params.originType);
    append(search, 'status', params.status);
    const response = await fetch(`/api/v1/cell-lines?${search}`, { credentials: 'include' });
    if (response.status === 401) {
      window.location.assign('/login');
      return { data: [], success: false, total: 0 };
    }
    if (!response.ok) throw new Error(t('search.loadFailed'));
    const body = (await response.json()) as { items: CellLine[] };
    return { data: body.items, success: true, total: body.items.length };
  };
  const requestTransactions = async (params: Record<string, unknown>) => {
    const search = new URLSearchParams({ limit: '100' });
    append(search, 'query', query);
    if (params.operationType !== allOperationTypes) append(search, 'operationType', params.operationType);
    append(search, 'storageLocationId', params.storageLocationId);
    append(search, 'operator', params.operator);
    const response = await fetch(`/api/v1/inventory-transactions?${search}`, { credentials: 'include' });
    if (response.status === 401) {
      window.location.assign('/login');
      return { data: [], success: false, total: 0 };
    }
    if (!response.ok) throw new Error(t('search.loadFailed'));
    const body = (await response.json()) as { items: Transaction[] };
    return { data: body.items, success: true, total: body.items.length };
  };

  const storageFieldProps = {
    treeData: storageTreeData,
    treeDefaultExpandedKeys: storageTreeExpandedKeys,
    popupMatchSelectWidth: 280,
    treeLine: true,
    treeNodeFilterProp: 'title',
    treeNodeLabelProp: 'pathLabel',
    showSearch: true,
  };
  const sampleColumns: ProColumns<Sample>[] = [
    { title: t('search.sampleNo'), dataIndex: 'sampleNo', search: false },
    { title: t('search.barcode'), dataIndex: 'barcode', search: false },
    {
      title: t('search.cellLine'),
      dataIndex: 'cellLineId',
      valueType: 'select',
      valueEnum: Object.fromEntries(cellLines.map((item) => [item.id, { text: item.code }])),
      render: (_, row) => cellLineById.get(row.cellLineId)?.code ?? row.cellLineId,
    },
    {
      title: t('search.material'),
      dataIndex: 'cellMaterialId',
      valueType: 'select',
      valueEnum: Object.fromEntries(materials.map((item) => [item.id, { text: item.name }])),
      render: (_, row) => row.materialName ?? materialById.get(row.cellMaterialId ?? '')?.name ?? '-',
    },
    {
      title: t('search.status'),
      dataIndex: 'status',
      valueType: 'select',
      valueEnum: Object.fromEntries(sampleStatuses.map((value) => [value, { text: sampleStatusLabel(value) }])),
      render: (_, row) => <Tag>{sampleStatusLabel(row.status)}</Tag>,
    },
    {
      title: t('search.location'),
      dataIndex: 'storageLocationId',
      valueType: 'treeSelect',
      hideInTable: true,
      fieldProps: storageFieldProps,
    },
    {
      title: t('search.location'),
      dataIndex: 'storagePositionId',
      search: false,
      render: (_, row) => positionById.get(row.storagePositionId ?? '')?.displayPath ?? positionById.get(row.storagePositionId ?? '')?.path ?? row.storagePositionId ?? '-',
    },
    {
      title: t('search.qcStatus'),
      dataIndex: 'qcStatus',
      valueType: 'select',
      valueEnum: Object.fromEntries(qcStatuses.map((value) => [value, { text: qcStatusLabel(value) }])),
      render: (_, row) => qcStatusLabel(row.qcStatus),
    },
    {
      title: t('search.actions'),
      fixed: 'right',
      width: 100,
      search: false,
      render: (_, row) => (
        <Space size="small">
          {canRequestCheckout && row.status === 'AVAILABLE' ? (
            <Button type="link" icon={<SendOutlined />} onClick={() => requestCheckout([row])}>
              {t('search.requestCheckout')}
            </Button>
          ) : null}
          <Button type="link" onClick={() => void navigate(`/samples?query=${encodeURIComponent(row.sampleNo)}`)}>
            {t('search.view')}
          </Button>
        </Space>
      ),
    },
  ];
  const cellLineColumns: ProColumns<CellLine>[] = [
    { title: t('search.code'), dataIndex: 'code', search: false },
    { title: t('search.name'), dataIndex: 'nameZh', search: false, render: (_, row) => row.nameZh ?? row.nameEn ?? '-' },
    { title: t('search.species'), dataIndex: 'species', valueType: 'select', valueEnum: Object.fromEntries(speciesOptions.map((value) => [value, { text: value }])) },
    { title: t('search.originType'), dataIndex: 'originType', valueType: 'select', valueEnum: Object.fromEntries(originTypes.map((value) => [value, { text: originTypeLabel(value) }])), render: (_, row) => originTypeLabel(row.originType) },
    { title: t('search.status'), dataIndex: 'status', valueType: 'select', valueEnum: { ACTIVE: { text: t('cellLines.statusOptions.ACTIVE') }, INACTIVE: { text: t('cellLines.statusOptions.INACTIVE') } }, render: (_, row) => <Tag>{t(`cellLines.statusOptions.${row.status}`, { defaultValue: row.status })}</Tag> },
    { title: t('search.actions'), fixed: 'right', width: 100, search: false, render: (_, row) => <Button type="link" onClick={() => void navigate(`/cell-lines?query=${encodeURIComponent(row.code)}`)}>{t('search.view')}</Button> },
  ];
  const transactionColumns: ProColumns<Transaction>[] = [
    {
      title: t('search.requestContent'),
      key: 'requestContent',
      width: 160,
      ellipsis: true,
      search: false,
      render: (_, row) =>
        Array.from(
          new Set(
            row.items
              .flatMap((item) =>
                [item.cellLineCode, item.materialName, item.passage].filter(
                  Boolean,
                ),
              )
              .map(String),
          ),
        ).join(' · ') || '-',
    },
    {
      title: t('search.operator'),
      dataIndex: 'executedByName',
      width: 100,
      search: false,
      render: (_, row) => row.executedByName ?? '-',
    },
    {
      title: t('search.operation'),
      dataIndex: 'operationType',
      width: 90,
      valueType: 'select',
      initialValue: allOperationTypes,
      valueEnum: Object.fromEntries(
        [allOperationTypes, ...operationTypes].map((value) => [
          value,
          {
            text:
              value === allOperationTypes
                ? t('search.allTypes')
                : operationLabel(value),
          },
        ]),
      ),
      render: (_, row) => (
        <Tag color={row.operationType === 'CONSUME' ? 'orange' : 'blue'}>
          {operationLabel(row.operationType)}
        </Tag>
      ),
    },
    {
      title: t('search.executedAt'),
      dataIndex: 'executedAt',
      width: 120,
      search: false,
      render: (_, row) => formatTenantDate(row.executedAt),
    },
    {
      title: t('search.items'),
      dataIndex: 'items',
      width: 90,
      search: false,
      render: (_, row) => row.items.length,
    },
    {
      title: t('search.requestNo'),
      dataIndex: 'requestNo',
      width: 150,
      search: false,
      render: (_, row) => row.requestNo ?? '-',
    },
    {
      title: t('search.transactionNo'),
      dataIndex: 'transactionNo',
      width: 170,
      search: false,
    },
    {
      title: t('search.location'),
      dataIndex: 'storageLocationId',
      valueType: 'treeSelect',
      hideInTable: true,
      fieldProps: storageFieldProps,
    },
    {
      title: t('search.operator'),
      dataIndex: 'operator',
      valueType: 'text',
      hideInTable: true,
    },
    {
      title: t('search.actions'),
      fixed: 'right',
      width: 100,
      search: false,
      render: (_, row) => (
        <Button
          type="link"
          onClick={() =>
            void navigate(`/transactions?query=${encodeURIComponent(row.transactionNo)}`)
          }
        >
          {t('search.view')}
        </Button>
      ),
    },
  ];

  const tabItems = [
    canReadSamples ? { key: 'samples', label: t('search.samples'), children: <ProTable<Sample>
      rowKey="id"
      columns={sampleColumns}
      request={requestSamples}
      params={{ query }}
      rowSelection={canRequestCheckout ? {
        onChange: (_, rows) => setSelectedSamples(rows),
        getCheckboxProps: (row) => ({ disabled: row.status !== 'AVAILABLE' }),
      } : false}
      toolBarRender={() => canRequestCheckout && selectedSamples.length > 0 ? [
        <Button
          key="request-checkout"
          type="primary"
          icon={<SendOutlined />}
          onClick={() => requestCheckout(selectedSamples)}
        >
          {t('search.requestCheckout')}
        </Button>,
      ] : []}
      search={{ defaultCollapsed: false, labelWidth: 'auto' }}
      options={{ reload: true, density: true, setting: true, fullScreen: true }}
      pagination={{ defaultPageSize: 50 }}
      scroll={{ x: 'max-content' }}
      tableLayout="fixed"
      cardBordered
    /> } : null,
    canReadCellLines ? { key: 'cell-lines', label: t('search.cellLines'), children: <ProTable<CellLine> rowKey="id" columns={cellLineColumns} request={requestCellLines} params={{ query }} search={{ defaultCollapsed: false, labelWidth: 'auto' }} options={{ reload: true, density: true, setting: true, fullScreen: true }} pagination={{ defaultPageSize: 50 }} scroll={{ x: 'max-content' }} cardBordered /> } : null,
    canReadTransactions ? { key: 'transactions', label: t('search.transactions'), children: <ProTable<Transaction> rowKey="id" columns={transactionColumns} request={requestTransactions} params={{ query }} search={{ defaultCollapsed: false, labelWidth: 'auto' }} options={{ reload: true, density: true, setting: true, fullScreen: true }} pagination={{ defaultPageSize: 50 }} scroll={{ x: 'max-content' }} cardBordered /> } : null,
  ].filter(Boolean) as { key: string; label: string; children: React.ReactNode }[];

  if (!currentUser) return <Spin fullscreen />;
  return (
    <section>
      <div className="search-filter-card">
        <Input.Search
          className="search-filter-row__input"
          size="large"
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          onSearch={submitQuery}
          enterButton={t('search.submit')}
          placeholder={t('search.placeholder')}
          allowClear
        />
      </div>
      <Tabs activeKey={activeTab} items={tabItems} onChange={changeTab} destroyInactiveTabPane />
    </section>
  );
}
